/** reason why we are using w3cwebsocket https://github.com/enisdenjo/graphql-ws/discussions/215 */
import { ApolloClient, ApolloLink, from, fromPromise, HttpLink, split } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { w3cwebsocket } from 'websocket';

import { createPosthogIdLink } from '@pflegia/feature-flags/apollo/posthog-id.link';
import { ACCOUNT_NOT_FOUND, DIRECT_ACCESS_EXPIRED, UNAUTHORIZED } from '@pflegia/types/api/errors/auth';
import { ACCESS_TOKEN_COOKIE_KEY, REFRESH_TOKEN_COOKIE_KEY } from '@pflegia/types/common/constants/tokens';
import { refererLink } from '@pflegia/ui/common/apollo/referer.link';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';
import { isJwtExpired } from '@pflegia/util/common/is-jwt-expired';
import { createRecaptchaLink, RecaptchaSupportedVersions } from '@pflegia/util/functions/create-apollo-recaptcha-link';
import { getSourceDomainFromClient } from '@pflegia/util/pflegia/source-domain/get-source-domain-from-client';
import { transformBackendEndpoint } from '@pflegia/util/pflegia/source-domain/transform-backend-endpoint';

import { LOCALES } from '@shared/constants/locale';

import { accessTokenService } from '@utils/access-token.service';

import { appAuthService } from 'domains/login/application/auth-service';

import { cache } from './apollo-cache';
import throttleLink from './graphql/throttle.link';

const unauthorizedErrorLink = onError(errors => {
  const invalidTokenError = (errors.graphQLErrors || []).find(({ message }) =>
    [ACCOUNT_NOT_FOUND, DIRECT_ACCESS_EXPIRED, UNAUTHORIZED].includes(message),
  );
  if (invalidTokenError) appAuthService.logout();
});

const refreshLink = new ApolloLink((operation, forward) => {
  const refreshToken = cookieStorage.getItem(REFRESH_TOKEN_COOKIE_KEY);
  const accessToken = accessTokenService.get();

  if (refreshToken && accessToken && isJwtExpired(accessToken)) {
    return fromPromise(appAuthService.refreshTokenFromApi()).flatMap(() => forward(operation));
  }

  return forward(operation);
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: transformBackendEndpoint(process.env.NEXT_PUBLIC_BACKEND_WEBSOCKET, getSourceDomainFromClient()) || 'ws://localhost:4001/graphql',
    webSocketImpl: w3cwebsocket,
    connectionParams: () => ({ cookies: { [ACCESS_TOKEN_COOKIE_KEY]: accessTokenService.get() } }),
  }),
);

export const getApolloClient = (locale: string = LOCALES[0]) =>
  new ApolloClient({
    cache,
    devtools: { enabled: process.env.NEXT_PUBLIC_ENV === 'development' },
    link: split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      from([
        refreshLink,
        refererLink,
        createPosthogIdLink(),
        createRecaptchaLink(RecaptchaSupportedVersions.V2),
        createRecaptchaLink(RecaptchaSupportedVersions.V3),
        throttleLink,
        unauthorizedErrorLink.concat(
          new HttpLink({
            credentials: 'include',
            headers: {
              'Accept-Language': locale,
            },
            uri: transformBackendEndpoint(process.env.NEXT_PUBLIC_BACKEND_ENDPOINT, getSourceDomainFromClient()),
          }),
        ),
      ]),
    ),
    ssrMode: false,
  });
